<template>
  <div class="container-fluid">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Cuentas corrientes por vendedor</h1>
          <p class="view-header__subtitle">Resumen de movimientos agrupados por cliente, segmentado por vendedor.</p>
        </div>
      </div>
    </div>

    <!-- STATS -->
    <div class="row">
      <div class="col-12 col-md-4">
        <div class="stats-card">
          <div class="stats-card__value">{{ formatoDinamicoMoneda(acumulado_debe) }}</div>
          <div class="stats-card__label">Debe acumulado</div>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="stats-card">
          <div class="stats-card__value">{{ formatoDinamicoMoneda(acumulado_haber) }}</div>
          <div class="stats-card__label">Haber acumulado</div>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="stats-card">
          <div
            class="stats-card__value"
            :class="acumulado_saldo < 0 ? 'stats-card__value--danger' : 'stats-card__value--success'"
          >
            {{ formatoDinamicoMoneda(acumulado_saldo) }}
          </div>
          <div class="stats-card__label">Saldo acumulado</div>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <q-select v-model="clerk_id" :options="salesClerkOptions" placeholder="Todos los vendedores" />
          </div>
          <div class="filters-panel__field">
            <q-select v-model.number="client_balance" :options="clientBalanceOptions" />
          </div>
          <div class="filters-panel__field">
            <label class="toggle-switch">
              <input v-model="historical" type="checkbox">
              <span class="toggle-switch__slider"></span>
              <span class="toggle-switch__text">Movimientos sin saldo</span>
            </label>
          </div>
          <div class="filters-panel__field filters-panel__field--inline">
            <label class="control-label">Fecha:</label>
            <Daterange :opendir="'right'" :filtered="true" @datepicked="dateCreated"/>
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn" @click="exportPDF">
              <i class="bi bi-filetype-pdf"></i>
              Exportar PDF
            </button>
            <button type="button" class="filters-panel__btn" @click="exportNominaPDF">
              <i class="bi bi-people"></i>
              Nómina PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
      />
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import formatMoneyMixin from "../../mixins/formatMoneyMixin";
import formatDateMixin from "../../mixins/formatDateMixin";
import Daterange from "../global/Daterange-component.vue";
import QSelect from '@/components/shared/QSelect.vue';
import { mapState } from 'vuex';

export default {
  components: {
    VdtnetTable,
    Daterange,
    QSelect,
  },
  mixins: [formatMoneyMixin, formatDateMixin],
  data: function () {
    return {
      acumulado_debe: 0,
      acumulado_haber: 0,
      acumulado_saldo: 0,
      startDate: null,
      endDate: null,
      clerk_id: null,
      client_balance: 0,
      historical: false,
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/reports/cccbyclerk",
          type: "GET",
          data: (d) => {
            d.daterange = this.daterange;
            d.clerk_id = this.clerk_id;
            d.client_balance = this.client_balance;
            d.historical = this.historical;
            return d;
          },
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        rowGroup: {
          startRender(rows, group) {
            return $('<tr/>').append('<td style="text-align:left;background-color: gray;color: white;" colspan="5">' + group + '</td>');
          },
          endRender(rows) {
            const totalDebe = rows.data().pluck('debe').reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
            const totalHaber = rows.data().pluck('haber').reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
            const totalSaldo = rows.data().pluck('saldo').reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
            return $('<tr/>').append('<td colspan="2"> Totales </td>')
              .append('<td>$ ' + $.fn.dataTable.render.number('.', ',', 2).display(totalDebe) + '</td>')
              .append('<td>$ ' + $.fn.dataTable.render.number('.', ',', 2).display(totalHaber) + '</td>')
              .append('<td>$ ' + $.fn.dataTable.render.number('.', ',', 2).display(totalSaldo) + '</td>');
          },
          dataSrc: ['client'],
        },
        processing: true,
        pageLength: 15,
        searching: true,
        searchDelay: 400,
        destroy: true,
        order: [[1, 'desc']],
        ordering: true,
        lengthChange: true,
        serverSide: true,
        autoWidth: false,
      },
      fields: {
        date: { label: "Fecha", render: this.formatoFechaHora },
        doc: { label: "Comprobante" },
        debe: { label: "Debe", align: "right", render: this.formatoDinamicoMoneda },
        haber: { label: "Haber", align: "right", render: this.formatoDinamicoMoneda },
        saldo: { label: "Saldo", align: "right", render: this.formatoDinamicoMoneda },
      },
    };
  },
  computed: {
    ...mapState('salesClerks', {
      salesClerks: state => state.salesClerks,
    }),
    salesClerkOptions() {
      return this.salesClerks.map(clerk => ({ value: clerk.id, label: clerk.name }));
    },
    clientBalanceOptions() {
      return [
        { value: 0, label: 'Todos los clientes' },
        { value: 1, label: 'Clientes con saldo' },
        { value: 2, label: 'Clientes sin saldo' },
      ];
    },
    daterange() {
      if (this.startDate === null || this.endDate === null) return null;
      const startDate = this.formatoFecha2Daterange(this.startDate);
      const endDate = this.formatoFecha2Daterange(this.endDate);
      return `${startDate} - ${endDate}`;
    },
    filters() {
      return {
        daterange: this.daterange,
        clerk_id: this.clerk_id,
        client_balance: this.client_balance,
        historical: this.historical,
      };
    },
  },
  watch: {
    filters() {
      this.reload();
    },
  },
  created() {
    this.$store.dispatch('salesClerks/getSalesclerks');
  },
  mounted() {
    this.setDefaultDateRange();
    this.getTotals();
  },
  methods: {
    reload() {
      this.getTotals();
      this.refreshTable();
    },
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    dateCreated(date) {
      this.startDate = date.startDate;
      this.endDate = date.endDate;
    },
    getTotals() {
      this.$http.post("/api/salesclerks/getGeneralBalanceToDate", this.filters)
        .then((response) => {
          this.acumulado_debe = response.data.data.acumuladoDebe;
          this.acumulado_haber = response.data.data.acumuladoHaber;
          this.acumulado_saldo = response.data.data.acumuladoSaldo;
        })
        .catch((error) => {
          console.error('getTotals:', error);
        });
    },
    exportPDF() {
      this.$http.post("/api/reports/printcccBySalesclerk", this.filters);
    },
    exportNominaPDF() {
      this.$http.post("/api/reports/PDFNominaclientsbyclerk", this.filters);
    },
    setDefaultDateRange() {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 30);
      this.startDate = this.formatDate(startDate);
      this.endDate = this.formatDate(today);
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>
